import { Button } from "@fieldwork/ui/components/button";
import { Link, createFileRoute } from "@tanstack/react-router";

import { Evidence } from "@/components/evidence";
import { NotConnected, useServer } from "@/components/server-hooks";
import { ServerPage } from "@/components/server-shell";
import { Chip } from "@/components/ui-bits";
import { useAgentQuery } from "@/lib/act";
import { taskPath, useOpenPlane } from "@/lib/plane";
import { type Evt, type Op, when } from "@/lib/runs";

export const Route = createFileRoute("/_auth/servers/$id/tasks/$key")({
  component: TaskHistory,
});

type History = {
  key: string;
  items: (Op & { run: number; started_at: string | null })[];
  links: { source: string; account: string; ref: string; created_by: string; created_at: string }[];
  events: Record<string, Evt>;
};

const OP_LABEL: Record<string, string> = { create: "New task", update: "Update", comment: "Comment", reprioritize: "Priority", review: "In review", close: "Close" };
const num = (n: number) => n.toLocaleString("en-US");

/** "2026-10-17" -> "Fri 17 Oct"; anything else as given. */
function day(s: unknown) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s ?? ""));
  if (!m) return s ? String(s) : "";
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

/** One line for what an op set: “name” · priority · due date. */
function headline(o: Op) {
  const p = o.payload ?? {};
  return [
    p.name ? `“${String(p.name)}”` : "",
    p.priority && p.priority !== "none" ? String(p.priority) : "",
    p.target_date ? `due ${day(p.target_date)}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

function TaskHistory() {
  const { id, key } = Route.useParams();
  const server = useServer(id).data;
  const connected = !!server?.hub.connected;
  const q = useAgentQuery<History>(id, "task_history", { key }, connected);
  const plane = useOpenPlane(id);
  const back = (
    <Link to="/servers/$id/activity" params={{ id }} className="self-start font-mono text-xs text-[#A8A59D] no-underline">
      ← Activity
    </Link>
  );
  const path = taskPath(server?.hub.status?.plane_workspace, key);
  const items = [...(q.data?.items ?? [])].reverse(); // newest first
  // the task's name as the agent last wrote it
  const name = items.map((o) => o.payload?.name).find((n) => typeof n === "string" && n.trim()) as string | undefined;
  const links = q.data?.links ?? [];

  return (
    <ServerPage
      back={back}
      title={
        name ? (
          <span className="flex flex-wrap items-baseline gap-3">
            <span className="text-[15px] font-normal tracking-normal text-[#A8A59D]">{key}</span>
            <span>{name}</span>
          </span>
        ) : (
          key
        )
      }
      actions={
        <Button variant="outline" disabled={!connected || plane.isPending} onClick={() => plane.mutate(path)}>
          Open in Plane ↗
        </Button>
      }
    >
      {!connected && <NotConnected />}
      {q.error && <p className="text-sm text-red-500">{q.error.message}</p>}
      {connected && !q.data && !q.error && <p className="m-0 text-sm text-[#A8A59D]">Loading…</p>}
      {q.data && (
        <div className="flex flex-wrap items-start gap-7">
          <section aria-labelledby="h-tl" className="flex min-w-0 flex-[2_1_520px] flex-col">
            <h2 id="h-tl" className="m-0 mb-3.5 font-mono text-[15px] font-bold">
              What the agent did, and why
            </h2>
            {items.length === 0 ? (
              <p className="m-0 border-t border-[#262523] py-4 text-[#A8A59D]">No agent changes recorded for this task.</p>
            ) : (
              <div className="border-b border-[#262523]">
                {items.map((o, i) => {
                  const line = headline(o);
                  return (
                    <div key={i} className="flex gap-4 border-t border-[#262523] py-4">
                      <span className="flex w-[92px] shrink-0 flex-col font-mono text-xs text-[#A8A59D]">
                        <span>{when(o.started_at)}</span>
                        <Link to="/servers/$id/runs/$rid" params={{ id, rid: String(o.run) }}>
                          run {num(o.run)}
                        </Link>
                      </span>
                      <div className="flex min-w-0 flex-1 flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Chip value="info" label={OP_LABEL[o.op] ?? o.op} />
                          {line && <span className="font-medium">{line}</span>}
                        </div>
                        {o.reason && <span className="text-[#A8A59D]">{o.reason}</span>}
                        <Evidence ids={o.event_ids ?? []} events={q.data!.events} onPage />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="m-0 mt-3 text-[13px] text-[#A8A59D]">Changes you make yourself in Plane don’t appear here.</p>
          </section>

          <aside className="flex min-w-0 flex-[1_1_280px] flex-col gap-5">
            <section
              aria-labelledby="h-src"
              className="flex flex-col gap-1 rounded-md bg-[#1A1A19] px-[22px] py-[18px] shadow-[inset_0_0_0_1px_#2A2927]"
            >
              <h2 id="h-src" className="m-0 mb-2.5 font-mono text-[15px] font-bold">
                Linked sources
              </h2>
              {links.length === 0 ? (
                <p className="m-0 border-t border-[#262523] py-2.5 text-[13px] text-[#A8A59D]">No conversations linked to this task yet.</p>
              ) : (
                links.map((l, i) => (
                  <div key={i} className="flex flex-col gap-0.5 border-t border-[#262523] py-2.5">
                    <span className="font-mono text-xs text-[#A8A59D]">
                      {l.source} · {l.account}
                    </span>
                    <span className="break-words">{l.ref}</span>
                    {(l.created_by || l.created_at) && (
                      <span className="text-xs text-[#8C8981]">
                        linked {l.created_by ? `by ${l.created_by} ` : ""}
                        {l.created_at ? when(l.created_at) : ""}
                      </span>
                    )}
                  </div>
                ))
              )}
            </section>
          </aside>
        </div>
      )}
    </ServerPage>
  );
}
